import React, { useMemo, useCallback } from 'react';
import { Grid, IconButton, Tooltip, CircularProgress } from '@mui/material';
import Icon from '@mui/material/Icon';
import { useNavigate, useSearch } from '@tanstack/react-router';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import MDButton from 'src/components/md/MDButton';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import MetricsCard from './components/MetricsCard';
import MetricsCharts from './components/MetricsCharts';
import EmptyState from './components/EmptyState';
import DateRangePicker from './components/DateRangePicker';
import useEmailPOMetrics from 'src/api/business-intelligence/useEmailPOMetrics';
import Page from '@/components/page/Page';

interface DateRange {
  from?: string;
  to?: string;
}

// Helper function to calculate groupBy based on date range span
function calculateGroupBy(
  from: string | undefined,
  to: string | undefined,
): 'daily' | 'weekly' | 'monthly' {
  if (!from) return 'daily';

  const fromDate = new Date(from);
  const toDate = to ? new Date(to) : new Date();
  const daysDiff = Math.ceil(
    (toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (daysDiff < 30) return 'daily';
  if (daysDiff < 90) return 'weekly';
  return 'monthly';
}

const EmailPOMetricsDashboard: React.FC = () => {
  const search = useSearch({ from: '/authenticated/email-po-metrics' });
  const navigate = useNavigate({ from: '/email-po-metrics' });

  // Extract date range from URL (single source of truth)
  const dateRangeFromUrl = useMemo((): DateRange => {
    const { from, to } = search;

    // If no parameters, return default (last week)
    if (!from && !to) {
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return {
        from: weekAgo.toISOString().split('T')[0],
      };
    }

    return { from, to };
  }, [search]);

  // Calculate groupBy based on date range span
  const calculatedGroupBy = useMemo(() => {
    return calculateGroupBy(dateRangeFromUrl.from, dateRangeFromUrl.to);
  }, [dateRangeFromUrl.from, dateRangeFromUrl.to]);

  // Stable query parameters for react-query
  const queryParams = useMemo(() => {
    const params: {
      from?: string;
      to?: string;
      groupBy?: 'daily' | 'weekly' | 'monthly';
    } = {};
    if (dateRangeFromUrl.from) params.from = dateRangeFromUrl.from;
    if (dateRangeFromUrl.to) params.to = dateRangeFromUrl.to;
    params.groupBy = calculatedGroupBy;
    return params;
  }, [dateRangeFromUrl.from, dateRangeFromUrl.to, calculatedGroupBy]);

  // API call with stable parameters
  const { data, isLoading, error, refetch } = useEmailPOMetrics(queryParams);

  // Handle date range changes - single event handler
  const handleDateRangeChange = useCallback(
    (newRange: DateRange) => {
      navigate({
        to: '.',
        search: {
          ...(newRange.from ? { from: newRange.from } : {}),
          ...(newRange.to ? { to: newRange.to } : {}),
        },
      });
    },
    [navigate],
  );

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Check if we have any data at all
  const hasData =
    data &&
    (data.salesOrdersCreatedByGently.totalCount > 0 ||
      data.famousErrors.totalCount > 0 ||
      data.activeUsers.totalCount > 0 ||
      data.activeCustomers.totalCount > 0);

  // Format current date range for display
  const formatDateRange = (range: DateRange): string => {
    const fromStr = range.from || 'Beginning';
    const toStr = range.to || 'Present';
    return `${fromStr} to ${toStr}`;
  };

  if (isLoading && !data) {
    return (
      <Page title="Email PO Ingestion Metrics">
        <MDBox py={3}>
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <MDBox textAlign="center">
              <CircularProgress size={40} />
              <MDTypography variant="body2" color="text" mt={2}>
                Loading metrics...
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Email PO Ingestion Metrics">
        <MDBox py={3}>
          <ErrorMessage error="Failed to load email PO metrics. Please try again." />
          <MDBox display="flex" justifyContent="center" mt={2}>
            <MDButton
              variant="outlined"
              color="primary"
              onClick={handleRefresh}
            >
              Retry
            </MDButton>
          </MDBox>
        </MDBox>
      </Page>
    );
  }

  return (
    <Page title="Email PO Ingestion Metrics">
      <MDBox mb={3}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} lg={8}>
            <MDTypography variant="body2" color="text" mt={1}>
              Monitor Gently&apos;s automated email processing performance and
              system health
            </MDTypography>
            <MDTypography variant="caption" color="text" mt={0.5}>
              Date Range: {formatDateRange(dateRangeFromUrl)}
            </MDTypography>
          </Grid>
          <Grid item xs={12} lg={4}>
            <MDBox display="flex" justifyContent="flex-end" gap={1}>
              <Tooltip title="Refresh Data">
                <IconButton onClick={handleRefresh} disabled={isLoading}>
                  <Icon>refresh</Icon>
                </IconButton>
              </Tooltip>
              {isLoading && data && <CircularProgress size={20} />}
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      <MDBox mb={3}>
        <DateRangePicker
          value={dateRangeFromUrl}
          onChange={handleDateRangeChange}
        />
      </MDBox>

      {!data || !hasData ? (
        <EmptyState
          icon="insights"
          title="No Data Available"
          message="No metrics data found for the selected date range. Try expanding your date range or check back later."
        />
      ) : (
        <>
          {/* Key Metrics Cards */}
          <MDBox mb={4}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricsCard
                  title="Sales Orders Created"
                  value={data.salesOrdersCreatedByGently.totalCount}
                  icon="shopping_cart"
                  iconColor="info"
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricsCard
                  title="Total Dollar Value"
                  value={data.salesOrdersCreatedByGently.totalValue || 0}
                  icon="attach_money"
                  iconColor="success"
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricsCard
                  title="System Errors"
                  value={data.famousErrors.totalCount}
                  icon="error"
                  iconColor={
                    data.famousErrors.totalCount > 0 ? 'error' : 'success'
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricsCard
                  title="Active Users"
                  value={data.activeUsers.totalCount}
                  icon="people"
                  iconColor="info"
                />
              </Grid>
            </Grid>
          </MDBox>

          {/* Charts */}
          <MetricsCharts data={data} loading={isLoading} />
        </>
      )}
    </Page>
  );
};

export default EmailPOMetricsDashboard;
